import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'

/** Задача в картці зміни: лише поля, потрібні рядку списку */
export interface ShiftAssignmentItem {
    id: number
    uuid: string
    status: string
    plannedLoadingDate?: string | null
    loadingPoint?: { title?: string | null } | null
    unloadingPoint?: { title?: string | null } | null
    executor?: {
        vehicleNeed?: {
            loadingPoint?: { title?: string | null } | null
            unloadingPoint?: { title?: string | null } | null
        } | null
    } | null
}

/** Скільки задач показати в картці; решту — лише лічильником */
export const SHIFT_ASSIGNMENTS_LIMIT = 100

/** Фільтр колекції задач за зміною */
export const buildShiftAssignmentsFilters = (shiftUuid: string) => ({
    and: [{ field: 'shift.uuid', op: '=', value: shiftUuid }],
})

/**
 * Задачі, прив'язані до зміни (поле «Зміна» задачі), у порядку створення.
 * Помилку завантаження показуємо, а не ховаємо за «Задач немає».
 */
export class ShiftAssignmentsStore {
    items: ShiftAssignmentItem[] = []
    total = 0
    loading = false
    error: string | null = null

    /** Відповідь на застарілий запит (перехід на іншу зміну під час завантаження) ігнорується */
    private requestId = 0

    /** Зміна, чиї задачі в items: при переході на іншу зміну старий список не показуємо */
    private shiftUuid = ''

    constructor() {
        makeAutoObservable<this, 'requestId' | 'shiftUuid'>(this, { requestId: false, shiftUuid: false })
    }

    async load(shiftUuid: string): Promise<void> {
        if (!shiftUuid) return
        const id = ++this.requestId
        if (shiftUuid !== this.shiftUuid) {
            this.shiftUuid = shiftUuid
            this.items = []
            this.total = 0
        }
        this.loading = true
        this.error = null
        try {
            // forceRefresh: задачу могли щойно прив'язати чи відв'язати в її картці
            const data = (await restClient.get(
                '/services/executor-assignments',
                {
                    searchParams: {
                        limit: SHIFT_ASSIGNMENTS_LIMIT,
                        filters: JSON.stringify(buildShiftAssignmentsFilters(shiftUuid)),
                        sort: JSON.stringify({ id: 'asc' }),
                    },
                },
                { forceRefresh: true }
            )) as { items?: ShiftAssignmentItem[]; meta?: { total?: number } } | null
            if (id !== this.requestId) return
            runInAction(() => {
                this.items = Array.isArray(data?.items) ? data.items : []
                this.total = data?.meta?.total ?? this.items.length
            })
        } catch {
            if (id !== this.requestId) return
            runInAction(() => {
                this.items = []
                this.total = 0
                this.error = 'Не вдалося завантажити задачі'
            })
        } finally {
            if (id === this.requestId) {
                runInAction(() => {
                    this.loading = false
                })
            }
        }
    }
}

/** Маршрут задачі: власні пункти standalone-задачі, інакше — пункти заявки */
export const assignmentRouteLabel = (item: ShiftAssignmentItem): string | null => {
    const from = item.loadingPoint?.title || item.executor?.vehicleNeed?.loadingPoint?.title
    const to = item.unloadingPoint?.title || item.executor?.vehicleNeed?.unloadingPoint?.title
    if (!from && !to) return null
    return `${from || '—'} → ${to || '—'}`
}
